"use client"

import { useMemo } from "react"
import { BarChart, Bar, XAxis, YAxis, Cell, LabelList } from "recharts"
import { IconCalendarOff } from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"

import type { AppointmentStats, AppointmentStatus } from "@/types"
import { APPOINTMENT_STATUSES, STATUS_HEX, useAppointmentStatusLabel } from "@/lib/appointment-status"

interface AppointmentsStatusChartProps {
  data: AppointmentStats | null
  isLoading: boolean
}

export function AppointmentsStatusChart({ data, isLoading }: AppointmentsStatusChartProps) {
  const t = useTranslations("dashboard")

  const statusLabel = useAppointmentStatusLabel()

  const chartData = useMemo(() => {
    if (!data?.by_status) return []
    return APPOINTMENT_STATUSES.map((status) => ({
      name: status,
      value: data.by_status[status] ?? 0,
      fill: STATUS_HEX[status],
    })).filter((item) => item.value > 0)
  }, [data])

  const chartConfig: ChartConfig = Object.fromEntries(
    APPOINTMENT_STATUSES.map((status) => [status, { label: statusLabel(status), color: STATUS_HEX[status] }])
  )

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("charts.appointmentsStatus.title")}>
        <div className="h-[200px] flex flex-col justify-center space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-5 flex-1 rounded" />
            </div>
          ))}
        </div>
      </ApolloPanel>
    )
  }

  if (!data || chartData.length === 0) {
    return (
      <ApolloPanel eyebrow={t("charts.appointmentsStatus.title")}>
        <div className="flex flex-col items-center justify-center h-[200px] text-center">
          <div className="rounded-full bg-muted p-3 mb-3">
            <IconCalendarOff className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("charts.appointmentsStatus.empty.title")}
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            {t("charts.appointmentsStatus.empty.body")}
          </p>
        </div>
      </ApolloPanel>
    )
  }

  return (
    <ApolloPanel eyebrow={t("charts.appointmentsStatus.title")}>
      <ChartContainer config={chartConfig} className="h-[200px] w-full">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ left: 0, right: 40, top: 8, bottom: 8 }}
        >
          <YAxis
            dataKey="name"
            type="category"
            tickLine={false}
            axisLine={false}
            width={80}
            tickFormatter={(value) => statusLabel(value)}
            tick={{ fontSize: 12 }}
          />
          <XAxis type="number" hide />
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel
                formatter={(value, name) => (
                  <div className="flex items-center gap-2">
                    <div
                      className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: STATUS_HEX[name as AppointmentStatus] }}
                    />
                    <span className="text-muted-foreground">
                      {statusLabel(name as AppointmentStatus)}
                    </span>
                    <span className="font-medium ml-auto">{value}</span>
                  </div>
                )}
              />
            }
          />
          <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={24}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
            <LabelList
              dataKey="value"
              position="right"
              className="fill-foreground"
              fontSize={12}
              offset={8}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
      <div className="flex items-center justify-center gap-4 mt-2">
        <span className="text-sm text-muted-foreground">
          {t("charts.appointmentsStatus.totalLabel")} <span className="font-medium text-foreground tabular-nums">{data.total ?? 0}</span>
        </span>
        <span className="text-sm text-muted-foreground">
          {t("charts.appointmentsStatus.thisMonthLabel")} <span className="font-medium text-foreground tabular-nums">{data.this_month ?? 0}</span>
        </span>
      </div>
    </ApolloPanel>
  )
}
